import React, { Component } from 'react';

import { debounce } from 'lodash-es';
import { connect } from 'react-redux';

import { setGlobalTheme, ThemeColorModes } from '@atlaskit/tokens';
import { FeatureKeys } from '@atlassian/bitbucket-features';

import { getTheme } from 'src/components/dev-asset-loader/version-dialog';
import { GlobalErrorBoundary } from 'src/components/error-boundary';
import isMobile from 'src/components/sidebar/src/is-mobile';
import { ScrollProvider } from 'src/components/sticky-header';
import { useDjangoFeature } from 'src/hooks/use-django-feature';
import toggleMobileHeader from 'src/redux/global/actions/toggle-mobile-header';
import { toggleUpToSmallBreakpoint } from 'src/redux/global/actions/toggle-responsive-breakpoint';
import { WINDOW_RESIZE_DEBOUNCE_DELAY } from 'src/sections/global/constants';
import {
  getIsMobileHeaderActive,
  getIsSiteMessageActive,
  getUserGlobalTheme,
} from 'src/selectors/global-selectors';
import { BucketDispatch, BucketState } from 'src/types/state';
import { isUpToSmallBreakpoint } from 'src/utils/is-responsive-breakpoint';

import * as styles from './page.styled';
import { SidebarPlaceholder } from './sidebar';

type StateProps = {
  isBannerOpen: boolean;
  isMobileHeaderActive: boolean;
  globalTheme?: string | null;
};

export type DispatchProps = {
  onWindowResize?: (shouldMobileHeaderDisplay: boolean) => void;
  toggleUpToSmall?: (shouldToggleToSmall: boolean) => void;
};

export type OwnProps = {
  children?: React.ReactNode;
  onWindowResize?: (shouldMobileHeaderDisplay: boolean) => void;
  toggleUpToSmall?: (shouldToggleToSmall: boolean) => void;
};

type Props = StateProps & DispatchProps & OwnProps;

// Need to access feature flag for theme render logic.
// Cannot call hooks in class components so creating a wrapper.
// Note: can completely remove wrapper & place logic inside render() on cleanup.
const RenderTheme = ({
  userPrefGlobalTheme,
}: {
  userPrefGlobalTheme?: string | null;
}) => {
  const isNavThemeSelectorEnabled = useDjangoFeature(
    FeatureKeys.isNavThemeSelectorEnabled
  );

  if (isNavThemeSelectorEnabled) {
    // Theme picker on - set theme with user preference
    setGlobalTheme({
      colorMode: (userPrefGlobalTheme as ThemeColorModes) || 'light',
    });
  } else {
    setGlobalTheme({ colorMode: getTheme() });
  }

  return null;
};

export class Page extends Component<Props> {
  componentDidMount() {
    window.addEventListener('resize', this.debouncedOnWindowResize);
    this.onWindowResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.debouncedOnWindowResize);
  }

  previousWindowWidth: number | null = null;
  scrollEl: React.RefObject<HTMLDivElement> = React.createRef();

  onWindowResize = () => {
    if (this.props.onWindowResize) {
      const currentWindowWidth = window.innerWidth;
      if (this.previousWindowWidth !== currentWindowWidth) {
        this.previousWindowWidth = currentWindowWidth;
        this.props.onWindowResize(isMobile());
      }
    }
    if (this.props.toggleUpToSmall) {
      this.props.toggleUpToSmall(isUpToSmallBreakpoint());
    }
  };

  debouncedOnWindowResize = debounce(
    this.onWindowResize,
    WINDOW_RESIZE_DEBOUNCE_DELAY
  );

  render() {
    const { isBannerOpen, isMobileHeaderActive, globalTheme } = this.props;

    return (
      <GlobalErrorBoundary>
        <RenderTheme userPrefGlobalTheme={globalTheme} />
        <styles.PageLayout isMobileHeaderActive={isMobileHeaderActive}>
          <ScrollProvider elementRef={this.scrollEl}>
            <styles.Content ref={this.scrollEl} isBannerOpen={isBannerOpen}>
              {this.props.children}
            </styles.Content>
            {!isMobileHeaderActive && (
              <SidebarPlaceholder isBeingRenderedInsideMobileNav={false} />
            )}
          </ScrollProvider>
        </styles.PageLayout>
      </GlobalErrorBoundary>
    );
  }
}

const mapStateToProps = (state: BucketState): StateProps => ({
  isBannerOpen: getIsSiteMessageActive(state),
  isMobileHeaderActive: getIsMobileHeaderActive(state),
  globalTheme: getUserGlobalTheme(state),
});

export const mapDispatchToProps = (
  dispatch: BucketDispatch,
  props: OwnProps
): DispatchProps => ({
  onWindowResize:
    props.onWindowResize ||
    ((shouldMobileHeaderDisplay: boolean) =>
      dispatch(toggleMobileHeader(shouldMobileHeaderDisplay))),
  toggleUpToSmall:
    props.toggleUpToSmall ||
    ((shouldToggleUpToSmall: boolean) =>
      dispatch(toggleUpToSmallBreakpoint(shouldToggleUpToSmall))),
});

export default connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(Page);
